"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useAppSelector, selectLeadStatus } from "@repo/web-store";
import { trackComponent } from "@repo/analytics";
import { dlEventConstants } from "@repo/analytics/data-layer.constants";
import {
  ScripInfoData,
  IndexListItem,
  IndicesInsightsConfig,
} from "./Interface";
import { IndicesHeader } from "./IndicesHeader";
import { IndicesTabs } from "./IndicesTabs";
import { IndexValueDisplay } from "./IndexValueDisplay";
import { OpenInterestDisplay } from "./OpenInterestDisplay";
import { DaysRangeSlider } from "./DaysRangeSlider";
import { IndicesActions } from "./IndicesActions";
import {
  handleChartClick,
  handleOptionChainClick,
} from "./IndicesActionHandlers";
import StockListNotFound from "../ClpStockCards/StockListNotFound";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";
import {
  INDICES_INSIGHTS_CLICK_TYPE_BUTTON,
  INDICES_INSIGHTS_CLICK_TYPE_CARDS,
  INDICES_INSIGHTS_COMPONENT_NAME,
  INDICES_INSIGHTS_PROPERTY_TYPE,
  INDICES_INSIGHTS_PRODUCT_CATEGORY,
  INDICES_INSIGHTS_PRODUCT_NAME,
  INDICES_INSIGHTS_REFERENCE_ID,
  INDICES_INSIGHTS_SPRINT,
  INDICES_INSIGHTS_CAMPAIGN,
  INDICES_INSIGHTS_JOURNEY_TAG,
} from "./indicesInsightsTrackingConstants";

interface MarketIndicesDashboardClientProps {
  indicesList: IndexListItem[];
  config?: IndicesInsightsConfig;
  initialIndexSymbol: string;
  initialData: ScripInfoData | null;
  initialDataByToken: Record<number, ScripInfoData | null>;
}

const MarketIndicesDashboardClient: React.FC<
  MarketIndicesDashboardClientProps
> = ({
  indicesList,
  config,
  initialIndexSymbol,
  initialData,
  initialDataByToken,
}) => {
  const visibleIndices = indicesList.slice(0, 4);
  const [selectedIndex, setSelectedIndex] = useState<string>(
    initialIndexSymbol ?? visibleIndices[0]?.symbol ?? "",
  );
  const dataCacheRef = useRef<Record<number, ScripInfoData>>({});
  const leadStatus = useAppSelector(selectLeadStatus);
  const hasBfslAccount = leadStatus === "ETB";
  const componentPosition = config?.componentPosition ?? "1";
  const headingText = config?.heading ?? selectedIndex ?? "Indices insights";

  useEffect(() => {
    visibleIndices.forEach((index) => {
      const prefetchedData = initialDataByToken[index.token];
      if (prefetchedData) {
        dataCacheRef.current[index.token] = prefetchedData;
      }
    });
  }, [visibleIndices, initialDataByToken]);

  const getIndexBySymbol = useCallback(
    (symbol: string): IndexListItem | undefined =>
      indicesList.find((i) => i.symbol === symbol),
    [indicesList],
  );

  useEffect(() => {
    if (visibleIndices.length === 0) {
      return;
    }

    if (!getIndexBySymbol(selectedIndex)) {
      setSelectedIndex(visibleIndices[0]?.symbol ?? "");
    }
  }, [visibleIndices, selectedIndex, getIndexBySymbol]);

  const trackIndicesTabSwitch = useCallback(
    async (tabValue: string) => {
      try {
        const customerId = await getCustomerIdForAnalytics();

        await trackComponent({
          componentName: INDICES_INSIGHTS_COMPONENT_NAME,
          sectionTitle: headingText,
          eventType: dlEventConstants.CLICK,
          clickType: INDICES_INSIGHTS_CLICK_TYPE_CARDS,
          ctaText: tabValue,
          productCategory: INDICES_INSIGHTS_PRODUCT_CATEGORY,
          cleverTapOverrides: {
            EP_JOURNEY: INDICES_INSIGHTS_JOURNEY_TAG,
            EP_PROPERTY_TYPE: INDICES_INSIGHTS_PROPERTY_TYPE,
            EP_PRODUCT_NAME: INDICES_INSIGHTS_PRODUCT_NAME,
            EP_CAMPAIGN: INDICES_INSIGHTS_CAMPAIGN,
            EP_SPRINT: INDICES_INSIGHTS_SPRINT,
            EP_REFERENCE_ID: INDICES_INSIGHTS_REFERENCE_ID,
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            EP_SR_NUMBER: componentPosition,
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track indices tab switch", error);
      }
    },
    [componentPosition, headingText],
  );

  const trackIndicesActionButtonClick = useCallback(
    async (ctaText: string) => {
      try {
        const customerId = await getCustomerIdForAnalytics();

        await trackComponent({
          componentName: INDICES_INSIGHTS_COMPONENT_NAME,
          sectionTitle: headingText,
          eventType: dlEventConstants.CLICK,
          clickType: INDICES_INSIGHTS_CLICK_TYPE_BUTTON,
          ctaText,
          productCategory: INDICES_INSIGHTS_PRODUCT_CATEGORY,
          cleverTapOverrides: {
            EP_JOURNEY: INDICES_INSIGHTS_JOURNEY_TAG,
            EP_PROPERTY_TYPE: INDICES_INSIGHTS_PROPERTY_TYPE,
            EP_PRODUCT_NAME: INDICES_INSIGHTS_PRODUCT_NAME,
            EP_CAMPAIGN: INDICES_INSIGHTS_CAMPAIGN,
            EP_SPRINT: INDICES_INSIGHTS_SPRINT,
            EP_REFERENCE_ID: INDICES_INSIGHTS_REFERENCE_ID,
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            EP_SR_NUMBER: componentPosition,
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track indices action button click", error);
      }
    },
    [componentPosition, headingText],
  );

  const handleTabChange = (tabValue: string) => {
    if (tabValue === selectedIndex) return;
    setSelectedIndex(tabValue);
    void trackIndicesTabSwitch(tabValue);
  };

  if (visibleIndices.length === 0) {
    return (
      <StockListNotFound
        errorMessage="No indices available"
        stockNotFoundImgUrl="https://cms-assets.bajajfinserv.in/is/image/bajajfinance/no-stocks"
      />
    );
  }

  const tabs = visibleIndices.map((index) => ({
    label: index.symbol,
    value: index.symbol,
  }));

  const optionChainText = config?.actions?.optionChainCta ?? "OPTION CHAIN";
  const chartsText = config?.actions?.chartsCta ?? "CHARTS";

  return (
    <div className="flex flex-col gap-[12px]">
      {(() => {
        const selectedIndexData = visibleIndices.find(
          (i) => i.symbol === selectedIndex,
        );
        const selectedTabData =
          selectedIndexData &&
          initialDataByToken[selectedIndexData.token];
        return selectedTabData ? (
          <IndicesHeader
            expiry={selectedTabData.expiry}
            title={selectedIndex}
            heading={config?.heading}
          />
        ) : null;
      })()}
      <IndicesTabs
        tabs={tabs}
        selectedTab={selectedIndex}
        onTabChange={handleTabChange}
      />
      {visibleIndices.map((index) => {
        const tabData = initialDataByToken[index.token];
        const isSelected = selectedIndex === index.symbol;

        return (
          <div
            key={index.token}
            className={isSelected ? "block" : "hidden"}
            data-index-symbol={index.symbol}
          >
            {tabData ? (
              <>
                <IndexValueDisplay
                  symbol={tabData.symbol}
                  ltp={tabData.ltp}
                  chg={tabData.chg}
                  chgPer={tabData.chgPer}
                  pcr={tabData.putCallRatio}
                  pcrLabelPrefix={config?.indexValueDisplay?.pcrLabelPrefix}
                />
                <div className="flex flex-col gap-[12px] rounded-[4px] border border-[#E5E5E5] px-[16px] py-[12px]">
                  <OpenInterestDisplay
                    callOi={tabData.callOi}
                    putOi={tabData.putOi}
                    callLabel={config?.openInterest?.callLabel}
                    putLabel={config?.openInterest?.putLabel}
                    errorMessage={config?.openInterest?.errorMessage}
                    fallbackValue={config?.openInterest?.fallbackValue}
                  />
                  <div className="h-px w-full bg-gray-400"></div>
                  <DaysRangeSlider
                    lowPrice={tabData.lowPrice}
                    highPrice={tabData.highPrice}
                    ltp={tabData.ltp}
                    label={config?.daysRange?.label}
                  />
                </div>
                <div>
                  {hasBfslAccount && (
                    <IndicesActions
                      onOptionChainClick={() => {
                        void trackIndicesActionButtonClick(optionChainText);
                        handleOptionChainClick(tabData, config);
                      }}
                      onChartClick={() => {
                        void trackIndicesActionButtonClick(chartsText);
                        handleChartClick(tabData, config);
                      }}
                      optionChainLabel={config?.actions?.optionChainCta}
                      chartsLabel={config?.actions?.chartsCta}
                    />
                  )}
                </div>
              </>
            ) : (
              <StockListNotFound
                errorMessage="No indices available"
                stockNotFoundImgUrl="https://cms-assets.bajajfinserv.in/is/image/bajajfinance/no-stocks"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MarketIndicesDashboardClient;
 